import { createFileRoute } from "@tanstack/react-router";
export const Route = createFileRoute("/admin/_layout/crm/calls")({
  component: CallsPage,
});

import { useState, useEffect, useMemo } from "react";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import { CallTable } from "@/components/crm/tables/CallTable";
import {
  useDataTableOrdering,
  type SortDirection,
} from "@/hooks/ui/useDataTableOrdering";
import {
  CallFormSheet,
  CALL_STATUSES,
} from "@/components/crm/forms/CallFormSheet";
import {
  useFetchCalls,
  useCallMutations,
  type CallOrdering,
} from "@/hooks/api/crm/useCalls";
import SearchFilter from "@/components/reusable-components/header/search-filter";
import { AssigneeFilter } from "@/components/crm/AssigneeFilter";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useDebounce } from "@/hooks/use-debounce";
import type { CrmCall, CrmCallPayload } from "@/types/crm";

function CallsPage() {
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, resetDebouncedSearch] = useDebounce(searchQuery, 500);
  const [statusFilter, setStatusFilter] = useState("");
  const [assignedTo, setAssignedTo] = useState<number | undefined>(undefined);
  const [showFilters, setShowFilters] = useState(false);

  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        subject: SortDirection;
        status: SortDirection;
        direction: SortDirection;
        duration_minutes: SortDirection;
        assigned_to__first_name: SortDirection;
      },
      CallOrdering
    >({
      subject: null,
      status: null,
      direction: null,
      duration_minutes: null,
      assigned_to__first_name: null,
    });

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingCall, setEditingCall] = useState<CrmCall | null>(null);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [callToDelete, setCallToDelete] = useState<CrmCall | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const callsQuery = useFetchCalls({
    page,
    page_size: pageSize,
    search: debouncedSearch || undefined,
    status: statusFilter || undefined,
    assigned_to: assignedTo ? String(assignedTo) : undefined,
    ordering: getApiOrdering(),
  });
  const calls = callsQuery.data?.results ?? [];
  const count = callsQuery.data?.count ?? 0;

  const { createCall, updateCall, deleteCall } = useCallMutations();

  const isMutating =
    createCall.isPending || updateCall.isPending || deleteCall.isPending;
  const isLoading = callsQuery.isPending || isMutating;
  const isFetching = callsQuery.isFetching || isMutating;

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(count / pageSize)),
    [count, pageSize]
  );

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, assignedTo, ordering, setPage]);

  const resetFilters = () => {
    setSearchQuery("");
    resetDebouncedSearch();
    setStatusFilter("");
    setAssignedTo(undefined);
    setOrdering({
      subject: null,
      status: null,
      direction: null,
      duration_minutes: null,
      assigned_to__first_name: null,
    });
    setPage(1);
  };

  const handlePageChange = (p: number) => {
    if (p >= 1 && p <= totalPages) {
      setPage(p);
    }
  };

  const onCreate = async (data: CrmCallPayload) => {
    await createCall.mutateAsync(data);
    toast.success("Call logged successfully!");
    setIsSheetOpen(false);
  };
  const onEdit = async (id: string, data: Partial<CrmCallPayload>) => {
    await updateCall.mutateAsync({ id, payload: data });
    toast.success("Call updated successfully!");
    setIsSheetOpen(false);
  };
  const handleDeleteConfirm = async () => {
    if (!callToDelete) return;
    try {
      await deleteCall.mutateAsync({ id: callToDelete.id });
      toast.success("Call deleted successfully!");
      setIsDeleteDialogOpen(false);
      setCallToDelete(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Calls"
          description="Log and manage calls."
          onToggleFilter={() => setShowFilters((p) => !p)}
          onAdd={() => {
            setEditingCall(null);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          addLabel="Log Call"
          isLoading={isFetching}
        />
        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <SearchFilter
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search calls..."
          />
          <AssigneeFilter value={assignedTo} onChange={setAssignedTo} className="w-[220px]" />
          <Select
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}
            disabled={isFetching}
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {CALL_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FiltersPanel>
        <CallTable
          calls={calls}
          onView={(c) => {
            setEditingCall(c);
            setIsViewOnly(true);
            setIsSheetOpen(true);
          }}
          onEdit={(c) => {
            setEditingCall(c);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          onDelete={(c) => {
            setCallToDelete(c);
            setIsDeleteDialogOpen(true);
          }}
          isLoading={isLoading}
          ordering={ordering}
          onOrderingChange={toggleOrdering}
        />
        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
        <CallFormSheet
          isOpen={isSheetOpen}
          isViewOnly={isViewOnly}
          onEditClick={isViewOnly ? () => setIsViewOnly(false) : undefined}
          onClose={() => setIsSheetOpen(false)}
          onCreate={onCreate}
          onEdit={onEdit}
          editingCall={editingCall}
          isLoading={isMutating}
        />
        <DeleteConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Call"
          description={`Delete call "${callToDelete?.subject}"?`}
          itemName={callToDelete?.subject}
          isLoading={deleteCall.isPending}
          destructive
        />
      </div>
    </SidebarInset>
  );
}
